import jsPDF from 'jspdf';

import {
  generatePDF,
  getDocSetup,
  imageToBase64,
} from '../../../helper/pdfGenerator';
import { PDFPage, PDFResult, PDFText } from '../../../models/pdfModal';

const getIntroDetails = (compName: string) => {
  return `The Board of Directors of the Company (“${compName}”) is pleased to present its Statement on Risk Management and Internal Control which has been prepared as referred by the Statement on Risk Management and Internal Control: Guidelines for Directors of Listed Issuers (‘the Guidelines”). This statement outlines the nature and state of the risk management and internal control of the Company during the period.`;
};

// #region Header footer
const controlReportHeader = async (
  isContinue: boolean = false
): Promise<PDFText[]> => {
  const address: string[] = [
    'Suite 8.03, Level 8, Menara AIA,',
    'Intan Milenium Square,',
    'No.68 Jalan Batai Laut 4,',
    'Taman Intan, 41300, Klang',
    'Selangor Darul Ehsan, Malaysia.',
  ];

  const allTexts: PDFText[] = [
    {
      id: '',
      value: '',
      x: 0,
      y: 19,
      box: {
        width: 16,
        height: 6.5,
        color: '#f8d24a',
        strokeColor: null,
      },
    },
    {
      id: '',
      value: await imageToBase64('/report/riskManagementLogo.png'),
      x: 36.5,
      y: 18,
      imageObject: {
        imageHeight: 69,
        imageWidth: 181,
        maxHeight: 11,
        maxWidth: 29,
      },
    },
    {
      id: '',
      value: 'Chartered Accounting Firm',
      x: 37.5,
      y: 21.5,
      fontsize: 5,
      textAlign: 'center',
    },
    ...address.map((add, ind): PDFText => {
      return {
        id: '',
        value: add,
        x: 37.5,
        y: 25 + ind * 2,
        fontsize: 5,
        textAlign: 'center',
      };
    }),
    {
      id: '',
      value: '',
      x: 59,
      y: 19,
      box: {
        width: 151,
        height: 6.5,
        color: '#314c9f',
        strokeColor: '',
      },
    },
    {
      id: '',
      value: 'Private & Confidential',
      x: 184.5,
      y: 17,
      textAlign: 'right',
      fontStyle: 'bolditalic',
      fontsize: 12,
    },
    {
      id: '',
      value: 'STATEMENT ON RISK MANAGEMENT AND INTERNAL CONTROL',
      x: 184.5,
      y: 36,
      textAlign: 'right',
      fontStyle: 'bold',
      maxWidth: 104,
      fontsize: 16,
      fontColor: '#2f5496',
    },
  ];

  if (isContinue) {
    allTexts.push({
      id: '',
      value: "(Cont'd)",
      x: 184.5,
      y: 47.5,
      textAlign: 'right',
      fontsize: 12,
    });
  }

  return allTexts;
};
const controlReportFooter = (
  currentPage: number,
  totalPage: number
): PDFText[] => {
  return [
    {
      id: '',
      value: 'Page',
      x: 173,
      y: 279,
      textAlign: 'right',
      fontsize: 12,
    },
    {
      id: '',
      value: `${currentPage}`,
      fontStyle: 'bold',
      x: 176.5,
      y: 279,
      textAlign: 'right',
      fontsize: 12,
    },
    {
      id: '',
      value: 'of',
      x: 181,
      y: 279,
      textAlign: 'right',
      fontsize: 12,
    },
    {
      id: '',
      value: `${totalPage}`,
      fontStyle: 'bold',
      x: 184.5,
      y: 279,
      textAlign: 'right',
      fontsize: 12,
    },
  ];
};
// #endregion Header footer

// #region Page 1
const controlReportIntro = (compName: string): PDFText[] => {
  return [
    {
      id: '',
      value: 'Introduction',
      x: 25.5,
      y: 54,
      maxWidth: 159,
      fontsize: 12,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: getIntroDetails(compName),
      x: 25.5,
      y: 64,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const controlReportBoard = (introLines: number): PDFText[] => {
  return [
    {
      id: '',
      value: 'Board Responsibility',
      x: 25.5,
      y: 94 + (introLines - 5) * 6.6,
      maxWidth: 159,
      fontsize: 12,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `The Board acknowledges its responsibility for maintaining a sound and effective systems of risk management and internal controls and for reviewing the adequacy and integrity of the said systems to ensure shareholders' interests and the Company's assets are safeguarded. These responsibilities have been delegated to the Audit Committee, which is empowered by its terms of reference to seek the assurance on the adequacy and effectiveness of the Company's internal controls system through independent reviews conducted by the internal audit function and the annual statutory audits conducted by the external auditors. However, as there are inherent limitations in any system of risk management and internal control, such system can only manage rather than eliminate all possible risks that may impede the achievement of the Company's business objectives or goals. Therefore, the system can only provide reasonable and not absolute assurance against material misstatements or losses.`,
      x: 25.5,
      y: 104 + (introLines - 5) * 6.6,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const controlReportRiskManagement = (introLines: number): PDFText[] => {
  return [
    {
      id: '',
      value: 'Risk Management Framework',
      x: 25.5,
      y: 174 + (introLines - 5) * 6.6,
      maxWidth: 159,
      fontsize: 12,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `In pursue of the Company's continuous commitment in optimising shareholders' value, risk management activities carried out across the Company is referred by the enhanced Enterprise Risk Management (“ERM”) Framework. The design of the ERM Framework is referred by ISO 31000, which outlines the risk governance and structure, risk management policies, risk management process and integration of risk management into significant activities and functions. Periodic review is performed on the ERM Framework and the relevant updates are made where necessary.`,
      x: 25.5,
      y: 184 + (introLines - 5) * 6.6,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `The risk assessment process provides an integrated and structured approach in identifying, evaluating and managing significant risks that may affect the achievement of the Company's business objectives. It promotes risk ownership and continuous monitoring of significant risks identified by way of assigning accountabilities to the respective Heads of Department and key management staffs. Significant risks identified are maintained in a formal database of risks and controls information i.e., risk registers, which captures the possible root causes, existing key controls and impact. The risks are then assessed on the likelihood of occurrence and criticality of impact with the rating of either low, medium, high or extreme.`,
      x: 25.5,
      y: 224 + (introLines - 5) * 6.6,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
// #endregion Page 1

// #region Page 2
const controlReportRiskManagementCont = (): PDFText[] => {
  return [
    {
      id: '',
      value: "Risk Management Framework (Cont'd)",
      x: 25.5,
      y: 54,
      maxWidth: 159,
      fontsize: 12,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `A Risk Management Committee (“RMC”) which consists of Senior Management and selected Heads of Department have the responsibilities to monitor the risk policy implementation, provide risk education to all staff, ensure accountability of risks identified are assigned and facilitate the risk reporting to the Board.`,
      x: 25.5,
      y: 64,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `The above-mentioned risk management practices of the Company serve as the on-going process used to identify, evaluate and managed significant risks of the Company for the year under review and up to the date of approval of this Statement. The Board shall continue to evaluate the Company's risk management process to ensure it remains relevant to the Company's requirements.`,
      x: 25.5,
      y: 89,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const controlReportOtherKeyElements = (): PDFText[] => {
  return [
    {
      id: '',
      value: 'Others Key Elements of Internal Control',
      x: 25.5,
      y: 119,
      maxWidth: 159,
      fontsize: 12,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `The other key elements of the Company's internal control systems are described below:`,
      x: 25.5,
      y: 129,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 143.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `   An organisational structure with clearly defined lines responsibility, accountability, and proper segregation of duties.`,
      x: 25.5,
      y: 144,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 158.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `  Written operational policies and procedures that are established and regularly reviewed and updated to ensure that it maintains its effectiveness and continues to support the Company's business activities as the Company's grows. `,
      x: 25.5,
      y: 159,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 178.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: ` Human resource policies encompassing areas of recruitment, training and development, health and safety, staff performance, appraisal and succession planning with the objective to enhance staff integrity and the development of professionalism and competency of employees in the Company. `,
      x: 25.5,
      y: 179,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 203.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `   Quality management in the form of policies and objectives as outlined in the Quality Manual issued by the Director. The Management Review Team periodically reviews this quality management process that is implemented throughout the financial year. `,
      x: 25.5,
      y: 204,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 223.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `   Monitoring of results by the senior management team on a monthly basis where appropriate management action will be undertaken to address deviations. The Director also reviews the management accounts covering financial performance, key business indicators on a quarterly basis and the cash flow position on a regular basis. `,
      x: 25.5,
      y: 224,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 253.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `   The Directors are actively involved in the running of the business and operations of the Company and they report to the Board on significant changes in the business and external environment, which affect the operations of the Company.`,
      x: 25.5,
      y: 254,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
// #endregion Page 2

// #region Page 3
const controlReportOtherKeyElementsCont = (): PDFText[] => {
  return [
    {
      id: '',
      value: "Others Key Elements of Internal Control (Cont'd)",
      x: 25.5,
      y: 54,
      maxWidth: 159,
      fontsize: 12,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `The other key elements of the Company's internal control systems are described below: (Cont'd)`,
      x: 25.5,
      y: 64,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 78.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `   The Company's risk management process, internal audit process and internal control system do not apply to jointly controlled operations and joint ventures where the Company does not have full management control. The Company's interest in these jointly controlled operations is closely monitored through periodic receipt of the operations management accounts and representation in the jointly controlled operations' Board.`,
      x: 25.5,
      y: 79,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `.`,
      x: 25.5,
      y: 113.5,
      fontsize: 30,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `   A set of Code of Ethics and Code of Conduct setting out expected ethical standards and code of conduct has been established, which is binding on all employees in the Company.`,
      x: 25.5,
      y: 114,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
const controlReportConclusion = (): PDFText[] => {
  return [
    {
      id: '',
      value: 'Conclusion',
      x: 25.5,
      y: 134,
      maxWidth: 159,
      fontsize: 12,
      fontStyle: 'bold',
    },
    {
      id: '',
      value: `The Board has obtained reasonable assurance from the Director that the Company's risk management and internal controls systems are operating adequately and effectively in all material respects`,
      x: 25.5,
      y: 144,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
    {
      id: '',
      value: `The Board is of the view that the risk management and internal control system is satisfactory and have not resulted in any material losses or contingencies that would require disclosure in the Company's annual report. The Board shall continue to take the pertinent measures to improve the Company's risk management and internal control in meeting the Company's corporate objectives.`,
      x: 25.5,
      y: 164,
      maxWidth: 159,
      fontsize: 12,
      textAlign: 'justify',
    },
  ];
};
// #endregion Page 3

export const generateRiskManagementReport = async (
  compName: string
): Promise<PDFResult> => {
  const doc = getDocSetup();

  const introLines = doc.splitTextToSize(getIntroDetails(compName), 159, {
    fontSize: 12,
  });

  const riskManagementDetails: PDFPage[] = [
    {
      background: '',
      texts: [
        ...(await controlReportHeader()),
        ...controlReportIntro(compName),
        ...controlReportBoard(introLines.length),
        ...controlReportRiskManagement(introLines.length),
        ...controlReportFooter(1, 3),
      ],
    },
    {
      background: '',
      texts: [
        ...(await controlReportHeader(true)),
        ...controlReportRiskManagementCont(),
        ...controlReportOtherKeyElements(),
        ...controlReportFooter(2, 3),
      ],
    },
    {
      background: '',
      texts: [
        ...(await controlReportHeader(true)),
        ...controlReportOtherKeyElementsCont(),
        ...controlReportConclusion(),
        ...controlReportFooter(3, 3),
      ],
    },
  ];

  return await generatePDF('Risk Management Report', riskManagementDetails);
};
